import { Divider, IconButton } from "@mui/material";
import React from "react";
import styled, { css, useTheme } from "styled-components";
import { theme } from "../../../Customization/Theme";
import { ConversationService } from "../../../Services/Apis";
import { HostWindowCommService } from "../../../Services/IframeHostService/HostWindowCommService";
import { OEvents } from "../../../Services/IframeHostService/models";
import { useAppDispatch, useAppSelector } from "../../../Store";
import {
  allMessagesStore,
  removeAllMessages,
  setIsSessionIdFromLocalStorage,
} from "../../../Store/Slices/socket/AllMessagesSlice";
import {
  botInfoStore,
  setOpenStatus,
  setToggleReloadWidget,
} from "../../../Store/Slices/socket/BotInfoSlice";
import {
  socketStatusStore,
} from "../../../Store/Slices/socket/SocketSlice";
import AppModel from "../../hooks/useModel/AppModel";
import useModal from "../../hooks/useModel/useModel";
import { PreviewType, SOCKET_CONNECTION_TYPES } from "../../utils/Enum";
import { EndChatFeedbackSchema } from "../../utils/ValidationSchema";
import { AppForm, AppInputRatingField, AppSubmitButton } from "../Forms";
import { AppMaterialIcons } from "../Icons";
import { useBotConfigStore } from "../../../Store/Slices/ChatbotSlices/BotConfigSlice";
import { sendDataToParent } from "../../utils/utils";
import { sanitizeString } from "../../utils/security/sanitizeHtml";

interface Props {

}

const AppEndChatFeedback: React.FC<Props> = () => {
    const theme:any=useTheme();
  const isNewUi = !!theme?.industry_style?.enabled;
  let commService = React.useMemo(() => {
    return HostWindowCommService.getInstance();
  }, []);

  const dispatch = useAppDispatch();
  const { isShowing, toggle } = useModal();
  // const model = useModal();

  const { socket } = useAppSelector(socketStatusStore);
  const { sessionId } = useAppSelector(allMessagesStore);
  const { botConfig } = useAppSelector(useBotConfigStore);
  const { visitorId, botId, widgetPreviewType, toggleReloadWidget, getaHost, } = useAppSelector(botInfoStore);
  const botHeaders = {
    appid: JSON.stringify({
      botid: botId,
    }),
    visitor_id: visitorId || null,
    preview: widgetPreviewType === PreviewType.getaWidgetPreview ? true : false,
    "GETA-HOST": getaHost,
  };

  // const useDrawer = useToggle();
  const [formStep, setFormStep] = React.useState<number>(1);
  const [userInput, setUserInput] = React.useState<any>({
    rating: 5,
    feedback: "",
  });

  const handleLiveDisconnectBtn = () => {
    // toggle(false);
    setFormStep(2);
    // useDrawer.toggle(true);
  };

  const handleCancel = () => {
    toggle(false);
    setFormStep(1);
    // useDrawer.toggle(false);
  };

  // Gated by the single `enhanced_bot` feature flag (per-bot widget config).
  // When enabled, "End Chat" runs the same end-conversation flow (feedback +
  // conversation-end actions) but keeps the existing session alive, so the user
  // can continue the same conversation later. When disabled → legacy "End
  // Conversation" flow, which also starts a fresh session.
  const enhancedBot = botConfig?.config?.enhanced_bot ?? false;

    const handleCloseChat = () => {
      const alwaysOpen = botConfig?.config?.always_open ?? false;
      !alwaysOpen && dispatch(setOpenStatus(false));
      
      const enableOnChatbotCloseEvent = botConfig?.config?.enable_on_chatbot_close_event ?? false;
      enableOnChatbotCloseEvent && sendDataToParent("onChatbotClose", "close");
    };

  const handleRestartBtn = () => {
    let data = {
      session_id: sessionId,
      rating: 5,
      feedback: '',
      // This modal ends the whole conversation, not just the live agent session, so
      // the agent must not be told the user is back with the assistant.
      end_conversation: true,
    };
    //inform the agent
    socket?.emit(SOCKET_CONNECTION_TYPES.CHAT_END_BY_USER, data);
    ConversationService.postChatFeedback(data, botHeaders)
      .then((res: any) => {
        const data = res?.data;
        // console.log("data", data);
      })
      .catch((err: any) => {
        console.error("submit error", err);
      })
      .finally(() => {
        dispatch(setOpenStatus(false));
        initiateBotSessionEvent();
        toggle(false);
      });
  };

  const handleReviewSubmit = (values: any, submitProps: any) => {
    let data = {
      session_id: sessionId,
      rating: values.rating,
      feedback: sanitizeString(values.feedback ?? ''),
      // Enhanced bot: "End Chat" ends only the live chat and hands the user back to
      // the bot on the same session, so this is not a full conversation end.
      end_conversation: !enhancedBot,
    };
    //inform the agent
    socket?.emit(SOCKET_CONNECTION_TYPES.CHAT_END_BY_USER, data);
    ConversationService.postChatFeedback(data, botHeaders)
      .then((res: any) => {
        const data = res?.data;
        // console.log("data", data);
      })
      .catch((err: any) => {
        console.error("submit error", err);
      })
      .finally(() => {
        submitProps?.setSubmitting(false);
        submitProps?.resetForm();
        setUserInput({ rating: 5, feedback: "" });
        // dispatch(setOpenStatus(false));
        handleCloseChat();
        // Enhanced bot keeps the existing session (no socket disconnect / reload).
        !enhancedBot && initiateBotSessionEvent();
        toggle(false);
        setFormStep(1);
      });
  };

  const handleNotReviewBtn = () => {
    let data = {
      session_id: sessionId,
      // rating: 5, // no ratings for no review btn
      feedback: null,
      // Enhanced bot: "End Chat" ends only the live chat and hands the user back to
      // the bot on the same session, so this is not a full conversation end.
      end_conversation: !enhancedBot,
    };
    ConversationService.postChatFeedback(data, botHeaders)
      .then((res: any) => {
        const data = res?.data;
        // console.log("data", data);
      })
      .catch((err: any) => {
        console.error("submit error", err);
    })
    //inform the agent
    socket?.emit(SOCKET_CONNECTION_TYPES.CHAT_END_BY_USER, data);
    setUserInput({ rating: 5, feedback: "" });
    // dispatch(setOpenStatus(false));
    handleCloseChat();
    // Enhanced bot keeps the existing session (no socket disconnect / reload).
    !enhancedBot && initiateBotSessionEvent();
    toggle(false);
    setFormStep(1);
  };

  const initiateBotSessionEvent = () => {
    commService.postMessage({
      event_type: OEvents.CHATBOT_SESSION_ID,
      data: null,
    });
    socket?.disconnect();
    dispatch(removeAllMessages());
    dispatch(setIsSessionIdFromLocalStorage(false));
    // setTimeout(() => {
    dispatch(setToggleReloadWidget(!toggleReloadWidget));
    // }, 10);
  };

  return (
    <>
      <IconButton onClick={() => toggle(true)}>
        <AppMaterialIcons
          iconName="Close"
          style={{
            color: theme?.components?.header.text.color,
            fontSize: "26px",
          }}
        />
      </IconButton>
      {/* Show Confirmation Model on reload incase of live agent */}
      <AppModel
        isShowing={isShowing}
        onClose={() => undefined}
        divStyle={{ padding: 0 }}
      >
        {formStep === 1 ? (
          <StyleWarningModelWrapper>
            {isNewUi ? (
              <span className="warnIcon" aria-hidden="true">
                <AppMaterialIcons iconName="WarningAmberRounded" />
              </span>
            ) : null}
            <p className="body">Are you sure you want to end conversation?</p>
            {isNewUi ? null : <Divider />}
            <div className={isNewUi ? "actions" : "footer"}>
              <button
                type="button"
                className={isNewUi ? "actionDanger" : "legacyPrimary"}
                onClick={() => handleLiveDisconnectBtn()}
              >
                {enhancedBot
                  ? isNewUi
                    ? "End chat"
                    : "End Chat"
                  : isNewUi
                    ? "End conversation"
                    : "End Conversation"}
              </button>
              {isNewUi ? (
                <>
                  <button
                    type="button"
                    className="actionOutline"
                    onClick={() => handleRestartBtn()}
                  >
                    Restart chat
                  </button>
                  <button
                    type="button"
                    className="actionGhost"
                    onClick={() => handleCancel()}
                  >
                    Not now
                  </button>
                </>
              ) : (
                <div className="footerAction">
                  <button
                    type="button"
                    className="legacyText"
                    onClick={() => handleRestartBtn()}
                  >
                    Restart Chat
                  </button>
                  <button
                    type="button"
                    className="legacyText"
                    onClick={() => handleCancel()}
                  >
                    Not now
                  </button>
                </div>
              )}
            </div>
          </StyleWarningModelWrapper>
        ) : null}
        {formStep === 2 && (
          <StyleRatingModelWrapper className="rating">
            <AppForm
              initialValues={userInput}
              onSubmit={(values: any, submitProps: any) => {
                handleReviewSubmit(values, submitProps);
              }}
              validationSchema={EndChatFeedbackSchema}
            >
              <AppInputRatingField />
              {isNewUi ? null : <Divider />}
              <div className={isNewUi ? "actions" : "footer"}>
                <AppSubmitButton
                  title={isNewUi ? "Submit feedback" : "Submit Feedback"}
                  className={isNewUi ? "actionAccent" : "legacyPrimary"}
                  style={
                    isNewUi
                      ? undefined
                      : { height: "38px", borderRadius: "8px", width: "100%" }
                  }
                />
                <button
                  type="button"
                  className={isNewUi ? "actionGhost" : "legacyText"}
                  onClick={() => handleNotReviewBtn()}
                >
                  {isNewUi ? "No, thanks" : "No, Thanks"}
                </button>
              </div>
            </AppForm>
          </StyleRatingModelWrapper>
        )}
      </AppModel>
    </>
  );
};

export default AppEndChatFeedback;

// Shared action stack for both steps. Full width and wrapping, so a long
// translation never sits side by side with another button and clips.
const actionStack = css`
  .actions {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .actionDanger,
  .actionOutline,
  .actionGhost,
  .actionAccent,
  .actions button {
    width: 100%;
    min-height: 44px;
    padding: 9px 12px;
    border-radius: 12px;
    font-family: ${({ theme }: any) => theme?.font?.primary?.family};
    font-size: 14.5px;
    font-weight: 600;
    line-height: 1.3;
    cursor: pointer;
    white-space: normal;
    overflow-wrap: anywhere;
    transition: background-color 0.15s ease;
  }

  .actionDanger {
    border: 0;
    background-color: #d64a41;
    color: #ffffff;

    &:hover {
      background-color: #bf3d35;
    }
  }

  .actionAccent {
    border: 0;
    background-color: ${({ theme }: any) => theme?.palette?.primary?.main};
    color: #ffffff;
  }

  .actionOutline {
    margin-top: 8px;
    min-height: 42px;
    font-size: 14px;
    font-weight: 500;
    border: 1px solid #e3e5f0;
    background-color: #ffffff;
    color: #20222e;

    &:hover {
      background-color: #f6f7fc;
    }
  }

  .actionGhost {
    margin-top: 8px;
    min-height: 36px;
    font-size: 13px;
    font-weight: 500;
    border: 0;
    background-color: transparent;
    color: ${theme.palette.default.darkGrey};

    &:hover {
      color: #20222e;
    }
  }
`;

const StyleWarningModelWrapper = styled.div`
  && {
    display: flex;
    flex-direction: column;
    min-height: 100px;
    border-radius: 4px;

    .body {
      padding: 10px 12px;
      flex: 1;
      overflow-y: auto;
      background-color: ${theme.palette.default.white};
      font-family: ${({ theme }: any) => theme?.font?.primary?.family};
      font-weight: 500;
      font-size: 1rem;
      letter-spacing: 0.5px;
      color: ${theme.palette.default.text};
      text-align: center;
      margin: 0;
    }

    .footerAction {
      display: flex;
      gap: 1rem;
      justify-content: center;
      align-items: center;
    }

    .footer {
      padding: 8px 16px;
      width: 100%;
      background-color: ${theme.palette.default.white};
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      text-align: center;
    }

    /* The two actions used to be AppSmallButtons; these reproduce them so the
       markup can be shared between both layouts. */
    .legacyPrimary,
    .legacyText {
      width: 100%;
      height: 38px;
      border-radius: 8px;
      font-family: ${({ theme }: any) => theme?.font?.primary?.family};
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
    }

    .legacyPrimary {
      border: 1px solid ${({ theme }: any) => theme?.palette?.primary?.main};
      background-color: ${({ theme }: any) =>
    theme?.components?.bot_options_standard?.normal?.background?.color};
      color: ${({ theme }: any) =>
    theme?.components?.bot_options_standard?.normal?.text?.color};
    }

    .legacyText {
      min-width: 114px;
      border: 0;
      background-color: transparent;
      color: ${theme.palette.default.darkGrey};
      white-space: nowrap;
    }
  }

  ${({ theme }: any) =>
    theme?.industry_style?.enabled &&
    css`
  && {
    align-items: center;
    text-align: center;
    padding: 22px 20px 16px;
    min-height: 0;

    .warnIcon {
      flex: none;
      width: 44px;
      height: 44px;
      margin-bottom: 14px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #fdeceb;

      .MuiSvgIcon-root {
        font-size: 22px;
        color: #d24b43;
      }
    }

    .body {
      margin: 0 0 18px;
      padding: 0 4px;
      font-family: ${({ theme }: any) => theme?.font?.primary?.family};
      font-weight: 600;
      font-size: 15.5px;
      line-height: 1.4;
      color: #20222e;
      overflow-wrap: anywhere;
    }

    ${actionStack}
  }
  `}
`;

const StyleRatingModelWrapper = styled.div`
  && {
    width: 330px;
    max-width: 100%;
    padding: 30px 12px;
  }

  ${({ theme }: any) =>
    theme?.industry_style?.enabled &&
    css`
      && {
        width: auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        padding: 22px 20px 16px;
      }

      ${actionStack}
    `}
`;


const StyleDrawerWrapper = styled.div`
  && {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    /* height: 100%; */
    padding: 1rem 0.5rem;
    margin: 0 2rem;
    border-radius: 0 0 8px 8px;
    background-color: ${theme.palette.default.white};
    border-radius: 4px;
    border: 1px solid;
    box-shadow: 1px 2px 10px rgba(0, 0, 0, 0.35);

    .header {
      padding: 4px 12px;

      width: 100%;
      height: 36px;
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08);
      background-color: ${theme.palette.default.white};

      font-size: 14.5px;
      font-weight: 500;
      letter-spacing: 0.5px;

      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
    }
    .footer {
      padding: 12px 16px 0px;
      width: 100%;

      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      gap: 0.5rem;
    }

    .body {
      padding: 10px 12px 10px;

      flex: 1;
      overflow-y: auto;
      background-color: ${theme.palette.default.white};

      font-size: 15.2px;
      font-weight: 400;
      line-height: 24px;
      color: ${theme.palette.default.text};
    }

    .section {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      border-bottom: 1px solid #ccc;
    }
  }
`;
